import type { HostError, HostValidation } from '@/lib/vscanner/types';

const fail = (title: string, hint: string, example: string): HostValidation => ({
  error: { title, hint, example } satisfies HostError,
});

/** Validates what the visitor typed and reduces it to a bare public hostname. */
export function normalizeHost(raw: string): HostValidation {
  let v = raw.trim();
  if (!v) return fail('Enter a website to check', 'Type the domain of the website you want to scan.', 'example.com');
  if (/\s/.test(v)) {
    return fail(
      'Domain cannot contain spaces',
      'Remove the spaces and enter one domain at a time.',
      'example.com',
    );
  }
  if (/^(ftp|file|ws|wss|mailto|tel|sms):/i.test(v)) {
    return fail(
      'Websites only',
      'We scan http:// and https:// pages. Other protocols (ftp, file, mailto…) cannot be checked.',
      'https://example.com',
    );
  }
  v = (v.replace(/^https?:\/\//i, '').split(/[/?#]/)[0].split('@').pop() ?? '').toLowerCase();
  const host = v.replace(/:\d+$/, '');
  if (!host) return fail('Enter a website to check', 'Type the domain of the website you want to scan.', 'example.com');
  if (
    /^\d{1,3}(\.\d{1,3}){3}$/.test(host) ||
    (/^\[?[0-9a-f:]+\]?$/i.test(host) && host.includes(':'))
  ) {
    return fail(
      'IP addresses cannot be scanned',
      'The free check only works with public domain names. Enter a domain instead of an IP.',
      'example.com',
    );
  }
  if (/^(localhost|.*\.(local|internal|lan|test|localhost))$/.test(host)) {
    return fail(
      'This looks like an internal address',
      'Only websites reachable from the public Internet can be scanned. Hosts such as localhost or *.local are not supported.',
      'example.com',
    );
  }
  if (!host.includes('.')) {
    return fail('Missing domain extension', 'A domain needs an extension such as .com, .vn or .io.', `${host}.com`);
  }
  if (/^\./.test(host) || /\.$/.test(host) || /\.\./.test(host)) {
    return fail(
      'Check the dots',
      'A domain cannot start or end with a dot, or contain two dots in a row.',
      'example.com',
    );
  }
  if (/[^a-z0-9.-]/.test(host)) {
    return fail(
      'Invalid characters',
      'Use only unaccented letters, digits, dots and hyphens — no symbols or punctuation.',
      'example.com',
    );
  }
  if (!/^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/.test(host)) {
    return fail(
      'Invalid domain',
      'Enter the domain as it appears in the browser address bar, without a path or parameters.',
      'example.com',
    );
  }
  return { host };
}
